import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchGetActiveUserProductCart } from "../../api/productCart";
import { CartType, CartLine } from "../../components/catalog/types";

type CartLineMap = Record<number, CartLine>;

export interface CartState {
  isCartLoading: boolean;
  activeCartData?: CartType;
  cartLineMap: CartLineMap;
}

const initialState: CartState = {
  isCartLoading: true,
  activeCartData: undefined,
  cartLineMap: {},
};

export const fetchCart = createAsyncThunk("cart/fetchCart", async () => {
  const result = await fetchGetActiveUserProductCart();
  if (!result.error) {
    return result.data.data;
  }
  return undefined;
});

const cartSlice = createSlice({
  name: "activeCart",
  initialState,
  reducers: {
    resetIsLoading: (state) => {
      state.isCartLoading = true;
    },
    updateCartLine: (state, action: PayloadAction<CartLine>) => {
      const line = action.payload;
      state.cartLineMap[line.product_id] = line;

      if (state.activeCartData) {
        const index = state.activeCartData.cart_lines.findIndex(
          (l) => l.product_id === line.product_id
        );
        if (index > -1) {
          state.activeCartData.cart_lines[index] = line;
        } else {
          state.activeCartData.cart_lines.push(line);
        }
      }
    },
    removeCartLine: (state, action: PayloadAction<number>) => {
      delete state.cartLineMap[action.payload];

      if (state.activeCartData) {
        state.activeCartData.cart_lines =
          state.activeCartData.cart_lines.filter(
            (line) => line.product_id !== action.payload
          );
      }
    },
    clearCart: (state) => {
      state.cartLineMap = {};
      if (state.activeCartData) {
        state.activeCartData.cart_lines = [];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.pending, (state) => {
        state.isCartLoading = true;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        const cartData = action.payload;
        state.activeCartData = cartData;
        state.isCartLoading = false;

        const newMap: CartLineMap = {};
        if (cartData?.cart_lines) {
          for (const line of cartData.cart_lines) {
            newMap[line.product_id] = line;
          }
        }

        state.cartLineMap = newMap;
      });
  },
});

export const { resetIsLoading, updateCartLine, removeCartLine, clearCart } =
  cartSlice.actions;

export default cartSlice.reducer;
